/* @jsx jsx */
import { useButton } from '@react-aria/button';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { useSelectState } from '@react-stately/select';
import { AriaSelectProps } from '@react-types/select';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { ListBox } from './OverlayListbox';
import { Popover } from './../../packages/base-elements/OverlayPopover';
import { FilterButton } from './../../components/lab/FilterButton';
import { AriaPositionProps, OverlayContainer, useOverlayPosition } from '@react-aria/overlays';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useOverlayTrigger } from '@react-aria/overlays';
import { useTranslation } from '@front10/utils';
import { useRect } from '@reach/rect';

export interface SelectProps<T> extends AriaSelectProps<T> {
  overlayPosition?: Omit<AriaPositionProps, 'onClose' | 'overlayRef' | 'targetRef'>;
  updateFocusAction?: () => void;
  popoverRef?: any;
}

function SelectCmp<T extends object>({ overlayPosition, ...props }: SelectProps<T>, ref: any) {
  // Create state based on the incoming props
  let state = useSelectState({
    ...props,
    onOpenChange: (isOpen) => {
      if (props?.updateFocusAction) {
        props?.updateFocusAction();
      }
    },
    onSelectionChange: () => {
      if (props?.updateFocusAction) {
        props?.updateFocusAction();
      }
    }
  });
  const overlayRef = React.useRef<HTMLDivElement>(null);

  // Get props for child elements from useSelect
  let ref_ = React.useRef<any>(null);
  let { triggerProps, menuProps, valueProps, labelProps } = useSelect(props, state, ref_);

  // Get props for the button based on the trigger props from useSelect
  let { buttonProps } = useButton(triggerProps, ref_);

  let overlayState = useOverlayTriggerState({
    isOpen: state.isOpen
  });

  const translate = useTranslation();

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  let { triggerProps: triggerProps_ } = useOverlayTrigger({ type: 'dialog' }, overlayState, ref_);

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps } = useOverlayPosition({
    targetRef: ref_,
    overlayRef,
    placement: 'bottom start',
    offset: 0,
    isOpen: state.isOpen,
    ...overlayPosition
  });

  const newPositionProps = {
    style: {
      ...positionProps.style,
      zIndex: 99999999999,
      background: 'white'
    }
  };

  const rect_ = ref_?.current?.getBoundingClientRect();
  const rect = useRect(ref_);
  React.useEffect(() => {
    if (state.isOpen) {
      state.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect_?.top]);
  const clickAction = React.useRef(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <VisuallyHidden {...labelProps}>{props.label}</VisuallyHidden>
      <VisuallyHidden id={'' + buttonProps.id + '-information'}>
        {state?.selectedItem?.textValue
          ? '' +
            translate('cmp-select-label-selected-item', 'Select') +
            ' ' +
            props.label +
            ' ' +
            state?.selectedItem?.textValue
          : translate('cmp-select-no-element-selected')}
      </VisuallyHidden>
      <HiddenSelect state={state} triggerRef={ref_} label={props.label} name={props.name} />
      <FilterButton
        {...buttonProps}
        onClick={(e: any) => {
          clickAction.current = true;
          if (buttonProps?.onClick) {
            buttonProps?.onClick(e);
          }
        }}
        aria-haspopup={undefined}
        aria-label={
          state?.selectedItem?.textValue
            ? '' +
              translate('cmp-select-label-selected-item', 'Select') +
              ' ' +
              props.label +
              ' ' +
              state?.selectedItem?.textValue
            : translate('cmp-select-no-element-selected')
        }
        aria-labelledby={'' + buttonProps.id + '-information'}
        ref={ref_}
        value={state.selectedItem.textValue}
        onFocus={(e: any) => {
          if (buttonProps?.onFocus) {
            buttonProps?.onFocus(e);
          }
          window.setTimeout(() => {
            if (props?.popoverRef?.current && clickAction.current === false) {
              const tmp = props?.popoverRef?.current;
              tmp.scrollTo(0, tmp.scrollHeight);
            }
            clickAction.current = false;
          }, 250);
        }}
      >
        <span {...valueProps}>
          {state.selectedItem ? state.selectedItem.rendered : props.label}
        </span>
      </FilterButton>
      {state.isOpen && (
        <OverlayContainer>
          <Popover
            {...newPositionProps}
            {...triggerProps_}
            isOpen={state.isOpen}
            onClose={() => {
              state.close();
            }}
            popoverRef={overlayRef}
            targetRef={ref_}
          >
            <ListBox {...menuProps} state={state} width={rect?.width} />
          </Popover>
        </OverlayContainer>
      )}
    </div>
  );
}

export const Select = React.forwardRef(SelectCmp);
